import { useEffect, useState, useSyncExternalStore } from 'react'
import { useApp } from './store'

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {}
      const mql = window.matchMedia(query)
      mql.addEventListener('change', cb)
      return () => mql.removeEventListener('change', cb)
    },
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false,
  )
}

/** False while the browser reports no network (offline banner; refetches resume on reconnect). */
export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window === 'undefined') return () => {}
      window.addEventListener('online', cb)
      window.addEventListener('offline', cb)
      return () => {
        window.removeEventListener('online', cb)
        window.removeEventListener('offline', cb)
      }
    },
    () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
    () => true,
  )
}

/** Desktop layout (sidebar) vs phone layout (pages + bottom bar). */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 900px)')
}

/** System preference OR the in-app Motion setting. */
export function useReducedMotion(): boolean {
  const system = useMediaQuery('(prefers-reduced-motion: reduce)')
  const pref = useApp((s) => s.motion)
  return system || pref === 'reduced'
}

/** A clock that ticks every `ms` while the page is visible. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setNow(Date.now())
    }, ms)
    const onVis = () => document.visibilityState === 'visible' && setNow(Date.now())
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [ms])
  return now
}
